import { Tag } from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { createInvoiceShowEditActions } from '../../../utils/createInvoiceShowEditActions';
import { useAuthContext } from '../../../contexts/AuthContext';
import { InvoiceStatus } from '../../../utils/constants/tagColors';
import { useErrorMessage } from '../../../utils/errorMessage';
import { useListResourceArchiving } from '../../../utils/listResourceArchiving';

/**
 * Hook pour obtenir les colonnes à afficher dans la liste des factures internes.
 * @function
 * @returns {Array} Les colonnes à afficher.
 */

export const useColumns = (setForceRefresh) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState();

  const { deleteResource, restoreResource } = useListResourceArchiving(
    setForceRefresh,
    'interninvoices/archiving'
  );

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/interninvoices/enums'
      });
      setEnums(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
  }, []);

  const columns = [
    {
      title: t('interninvoices.form.status'),
      key: 'status',
      dataIndex: ['status'],
      render: (status) =>
        status ? (
          <Tag color={InvoiceStatus[status]}>
            {t(`interninvoices.tags.${status}`)}
          </Tag>
        ) : (
          'N/R'
        ),
      sorter: true,
      filters: enums?.status?.map((r) => ({
        text: t(`interninvoices.tags.${r}`),
        value: r
      }))
    },
    {
      title: t('interninvoices.form.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true
    },
    {
      title: t('interninvoices.form.emitter'),
      key: 'emitter',
      dataIndex: 'emitter',
      sorter: (a, b) => a.emitter.length - b.emitter.length
    },
    {
      title: t('interninvoices.form.wording'),
      key: 'wording',
      dataIndex: 'wording',
      sorter: true
    },
    {
      title: t('interninvoices.form.emission_date'),
      key: 'emission_date',
      dataIndex: 'emission_date',
      sorter: true,
      render: (emission_date) =>
        emission_date && dayjs(emission_date).format('DD/MM/YYYY')
    },
    {
      title: t('interninvoices.form.payment_date'),
      key: 'payment_date',
      dataIndex: 'payment_date',
      sorter: true,
      render: (payment_date) =>
        payment_date ? dayjs(payment_date).format('DD/MM/YYYY') : '-'
    },
    {
      title: t('interninvoices.form.total_ttc'),
      key: 'total_ttc',
      dataIndex: 'total_ttc',
      render: (total_ttc) => (total_ttc ? `${total_ttc} €` : ''),
      sorter: true
    },
    {
      key: 'action',
      align: 'right',
      fixed: 'right',
      width: 120,
      render: (record) => (
        <>
          {createInvoiceShowEditActions({
            t,
            enums,
            record,
            deleteResource,
            restoreResource,
            showPath: '/invoices/intern/show',
            editPath: '/invoices/intern/edit'
          })}
        </>
      )
    }
  ];

  const headers = [
    {
      label: 'reference',
      key: 'reference'
    },
    {
      label: 'emitter',
      key: 'emitter'
    },
    {
      label: 'wording',
      key: 'wording'
    },
    {
      label: 'emission_date',
      key: 'emission_date'
    },
    {
      label: 'payment_date',
      key: 'payment_date'
    },
    {
      label: 'total_ttc',
      key: 'total_ttc'
    },
    {
      label: 'status',
      key: 'interninvoice_status'
    }
  ];

  return { columns, headers };
};
